import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function BlogArticle() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["blog", slug], queryFn: async () => (await api.get(`/blog/${slug}`)).data, retry: false,
  });
  useSeo({ title: data?.post?.title, description: data?.post?.excerpt, image: data?.post?.cover_image });

  if (isLoading) return <div className="bs-container py-24 text-center text-muted-foreground">Loading...</div>;
  if (isError || !data) return (
    <div className="bs-container py-24 text-center">
      <p className="text-muted-foreground">Article not found.</p>
      <Button asChild className="mt-4 rounded-full"><Link to="/blog">Back to blog</Link></Button>
    </div>
  );
  const { post, related } = data;

  return (
    <article className="bs-container py-16 lg:py-20">
      <div className="mx-auto max-w-3xl">
        <Link to="/blog" className="mb-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="mr-1.5 h-4 w-4" /> Blog</Link>
        <Badge variant="secondary">{post.category}</Badge>
        <h1 className="mt-4 font-display text-4xl font-black leading-tight sm:text-5xl">{post.title}</h1>
        <p className="mt-4 text-sm text-muted-foreground">By {post.author || "Buddy Solution"}</p>
        <img src={post.cover_image} alt={post.title} className="mt-8 w-full rounded-3xl border border-border object-cover shadow-soft" />
        <div className="prose prose-neutral mt-10 max-w-none dark:prose-invert">
          {post.content.split("\n\n").map((para, i) => (
            <p key={i} className="mb-5 text-base leading-relaxed text-foreground/90">{para}</p>
          ))}
        </div>
      </div>

      {related?.length > 0 && (
        <div className="mx-auto mt-16 max-w-5xl border-t border-border pt-12">
          <h2 className="font-display text-2xl font-bold">Related articles</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-3">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group overflow-hidden bs-card hover:shadow-soft">
                <img src={p.cover_image} alt={p.title} className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="p-4"><h3 className="font-display text-base font-bold">{p.title}</h3></div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mx-auto mt-16 max-w-3xl rounded-3xl border border-border bg-card/40 p-8 text-center">
        <h3 className="font-display text-xl font-bold">Have a project in mind?</h3>
        <Button asChild className="mt-4 rounded-full"><Link to="/contact">Start a Project <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
      </div>
    </article>
  );
}
